import { generateId } from "@evergis/api";
import React, { createContext, FC, useCallback, useContext, useMemo } from "react";
import { useAttributes } from "../../../../../hooks/attributes/useAttributes";
import { LayerInfo } from "../../../../../interfaces/resourceInfo";
import { LayerReferenceItem, LayerReferencesContextType } from "./types";

const LayerReferencesContext = createContext<LayerReferencesContextType>({});

export interface LayerReferencesProviderProps {
  layer: LayerInfo;
}

export const LayerReferencesProvider: FC<LayerReferencesProviderProps> = ({ layer, children }) => {
  const initialLayerReferences = useMemo(
    () =>
      layer.configuration.attributesConfiguration.layerReferences
        ? layer.configuration.attributesConfiguration.layerReferences.map(layerReference => ({
            id: generateId(),
            ...layerReference,
            referenceId: layerReference.referenceId || generateId(),
          }))
        : [
            {
              id: generateId(),
              layerName: null,
              referenceAttribute: null,
              targetAttribute: null,
              referenceId: generateId(),
            },
          ],
    [layer],
  );
  const [
    layerReferences,
    {
      updateAttribute: updateLayerReference,
      setAttributes: setLayerReferences,
      deleteAttribute: deleteLayerReference,
      addAttribute: addLayerReference,
    },
  ] = useAttributes<LayerReferenceItem>(initialLayerReferences);
  const createLayerReference = useCallback(() => {
    addLayerReference({
      id: generateId(),
      layerName: null,
      referenceAttribute: null,
      targetAttribute: null,
      referenceId: generateId(),
    });
  }, [addLayerReference]);

  const value: LayerReferencesContextType = {
    layerReferences,
    updateLayerReference,
    setLayerReferences,
    deleteLayerReference,
    addLayerReference,
    createLayerReference,
    layer,
  };

  return <LayerReferencesContext.Provider value={value}>{children}</LayerReferencesContext.Provider>;
};

export const useLayerReferencesContext = (): LayerReferencesContextType => {
  return useContext(LayerReferencesContext);
};
